import { CSSProperties, FC, useContext } from 'react';
import { filter, isEmpty } from 'ramda';

import Chip, { ChipProps } from '@material-ui/core/Chip';

import { KeycloakContext } from 'components/Secured';
import { PRODUCT_STATUS, SUPERUSER_APPROVAL } from 'components/constants-ts';
import IProduct from 'models/product';
import { isDefined } from 'utils/helpers';
import { ADMIN_REVIEW_STATUS_LABELS } from 'constants/domain';

export const getStatusChipProps = (
  product: IProduct,
  params: {
    isSuperuser: boolean;
    isProducer: boolean;
    isStore: boolean;
    notificationsData: any;
    currentCategory?: string;
  }
) => {
  const { isSuperuser, isProducer, isStore, notificationsData, currentCategory } = params;

  // Check if product has been updated.
  const notifications = isDefined(notificationsData)
    ? filter((n: any) => n.value === product.id, notificationsData)
    : [];
  const hasUpdateNotification = !isEmpty(notifications);

  // Badge information to producers.
  let chipProps: (ChipProps & { bgColor?: string; textColor?: string }) | undefined;
  if (product.isDraft) {
    chipProps = { label: 'Utkast', bgColor: '#e0e0e0', textColor: 'rgb(0 0 0 / 87%)' };
  } else if (product.inactive) {
    chipProps = { label: 'Inaktiv', bgColor: 'rgba(0, 0, 0, 0.87)', textColor: '#fff' };
  } else if (currentCategory === 'IN_REVIEW' && product.adminStatus === SUPERUSER_APPROVAL.AWAITING_APPROVAL) {
    chipProps = { label: ADMIN_REVIEW_STATUS_LABELS[SUPERUSER_APPROVAL.AWAITING_APPROVAL], bgColor: '#E8EAED', textColor: '#3C4043' };
  } else if (currentCategory === 'IN_REVIEW' && product.adminStatus === SUPERUSER_APPROVAL.ADMIN_DISAPPROVED) {
    chipProps = { label: ADMIN_REVIEW_STATUS_LABELS[SUPERUSER_APPROVAL.ADMIN_DISAPPROVED], bgColor: '#FFEAC2', textColor: '#946000' };
  } else if ((isSuperuser || isProducer || isStore) && product.adminStatus === SUPERUSER_APPROVAL.AWAITING_APPROVAL) {
    chipProps = { label: 'Under granskning', bgColor: '#FFEAC2', textColor: '#946000' };
  } else if ((isSuperuser || isProducer || isStore) && product.adminStatus === SUPERUSER_APPROVAL.ADMIN_DISAPPROVED) {
    chipProps = { label: 'Behöver åtgärd', bgColor: '#FFEBEE', textColor: '#B71C1C' };
  } else if (
    (isSuperuser || isProducer || isStore) &&
    product.adminStatus === SUPERUSER_APPROVAL.ADMIN_APPROVED &&
    !hasUpdateNotification
  ) {
    chipProps = { label: 'Godkänd', bgColor: '#D2EFDA', textColor: '#285943' };
  }

  // Let marked for removal.
  if ((isProducer || isStore) && product.productStatus === PRODUCT_STATUS.MARKED_FOR_REMOVAL) {
    chipProps = { label: 'Awaiting removal', color: 'primary' };
  }

  if (!chipProps && !product.isDraft && hasUpdateNotification) {
    chipProps = { label: 'Uppdaterad produkt!', bgColor: '#E8EAF6', textColor: '#4050B5' };
  }

  return chipProps;
};

interface ProductStatusChipProps {
  product: IProduct;
  notificationsData?: any;
  style?: CSSProperties;
  currentCategory?: string;
}

const ProductStatusChip: FC<ProductStatusChipProps> = ({ product, notificationsData, style, currentCategory }) => {
  const keycloakCtx = useContext(KeycloakContext);
  const isSuperuser = keycloakCtx.keycloak.hasResourceRole('superuser');
  const isProducer = keycloakCtx.keycloak.hasResourceRole('producer');
  const isStore = keycloakCtx.keycloak.hasResourceRole('store');

  const chipProps = getStatusChipProps(product, { isSuperuser, isProducer, isStore, notificationsData, currentCategory });

  if (!chipProps) return null;

  return (
    <Chip
      color="primary"
      {...chipProps}
      style={{
        ...style,
        fontWeight: 600,
        background: chipProps.bgColor,
        color: chipProps.textColor,
      }}
    />
  );
};

export default ProductStatusChip;
